import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { GET_QUERY_KEY, QUERY_OPTIONS } from "../../../common/constants";
import { IResponse, IServerError } from "../../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {

	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
		sortDDLByLabel,
} from "../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CRACreditService } from "../../../services/CRACreditService";
import { CompanyTD1FormService } from "../../../services/CompanyTD1FormService";
import { EDRReportDataGridWithColumnToggled } from "./EDRReportDataGridWithColumnToggled";
import { EDRReportHeader } from "./EDRReportHeader";
import { EmployeeService } from "../../../services/EmployeeService";
import FileSaver from "file-saver";
import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../models";
import { LookupFieldService } from "../../../services/LookupFieldService";
import { ReportsService } from "../../../services/ReportsService";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";

export interface IEDRReportSetupPage { }
const EDR_REPORT_QUERY_KEY = "edr-report";
const EMPLOYEE_DDL_DATA_QUERY_KEY = "employee-ddl-data-record";
const EDR_LIST_QUERY_KEY = "edr-profile-list";

const useEDRReport = (
	runGetListAPICall: boolean,
	clientId: string,
	employee: string,
	type: string,
	endDate: string,
	payPeriod: string,
	edr: string,
	taxYear: number,
	params: IGetListRequest,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			EDR_REPORT_QUERY_KEY,
			runGetListAPICall,
			clientId,
			employee,
			type,
			endDate,
			payPeriod,
			edr,
			taxYear,
			params,
		],
		queryFn: () =>
			ReportsService.getEDRReport(
				runGetListAPICall,
				clientId,
				employee,
				type,
				endDate,
				payPeriod,
				edr,
				taxYear,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});


export const useGetLookupFieldData = (lookupFieldId: string) =>
	useQuery({
		queryKey: [GET_QUERY_KEY.LOOKUP_FIELD_DATA_BY_ID, lookupFieldId],
		queryFn: (data) => {
			return LookupFieldService.getByIdd(lookupFieldId);
		},
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDLItems = (
	clientId: string,
	status: number,
	taxYear: number
) =>
	useQuery({
		queryKey: [EMPLOYEE_DDL_DATA_QUERY_KEY, clientId, status, taxYear],
		queryFn: (data) => {
			return CompanyTD1FormService.getEmployeeDDLByStatus(clientId, status, taxYear);
		},
		refetchOnWindowFocus: false,
	});


const useGetPayPeriodDDL = (
	clientId: string,
	employeePayPeriodId: string | null,
	taxYear: number
) =>
	useQuery({
		queryKey: [
			"periods-ddl",
			employeePayPeriodId,
		],
		queryFn: () => {
			return CRACreditService.getPayPeriodDDL(
				clientId,
				employeePayPeriodId,
				taxYear
			);
		},
		refetchOnWindowFocus: false,
	});


const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["pay-frequency-ddl"],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

export const EDRReport: React.FC<IEDRReportSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [edrReportData, setEDRReportData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);
	const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
	const [employeeStatus, setEmployeeStatus] = useState<number>(100);

	const {
		isPending: isPendingPayPeriodDDL,
		data: payPeriodDDL,
	} = useGetPayPeriodDDL(clientId, selectedPeriod !== "all" ? selectedPeriod : null, taxYear);

	const { isPending: isPendingPayFrequencyDDL, data: payFrequencyDDL } = useGetPayFrequencyDDL(clientId, taxYear);

	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const employee = formMethods.getValues("employee");
	const payPeriod = formMethods.getValues("payPeriod");
	const endDate = formMethods.getValues("endDate");
	const type = formMethods.getValues("type");
	const edr = formMethods.getValues("edr");

	const { isPending, data } = useEDRReport(
		runGetListAPICall,
		clientIdStore,
		employee,
		type,
		endDate,
		payPeriod,
		edr,
		taxYear,
		queryParams,
		navigate
	);
	const edrList: any | null = data;

	useEffect(() => {
		if (edrList) {
			setEDRReportData(edrList);
		}
	}, [edrList]);

	const { isPending: isPendingEmployeeDDLItems, data: employeeDDLItems } =
		useGetEmployeeDDLItems(clientId, employeeStatus, taxYear);

	const edrReportDataAPICall = (
		employee: string,
		type: string,
		endDate: string,
		payPeriod: string,
		edr: string,
	) => {
		const fetchData = async () => {
			setIsLoading(true);
			// setQueryParams({
			// 	search_query: "",
			// 	order_by: "name",
			// 	order_direction: "asc",
			// 	page: 1,
			// 	size: 25,
			// });
			try {
				const response: any = await ReportsService.getEDRReport(
					true,
					clientIdStore,
					employee,
					type,
					endDate,
					payPeriod,
					edr,
					taxYear,
					queryParams,
					navigateToLogout
				);

				if (response) {
					setEDRReportData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {
		edrReportDataAPICall(
			values.employee,
			values.type,
			values.endDate,
			values.payPeriod,
			values.edr,
		);
	};

	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.getEDRAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				setRunGetListAPICall(true)
				return queryClient.invalidateQueries({
					queryKey: [EDR_REPORT_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});


	useEffect(() => {
		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);

	const employeeDDlItems = useMemo(() => {
		if (employeeDDLItems) {
			if (
				employeeDDLItems &&
				Array.isArray(employeeDDLItems)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(employeeDDLItems).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [employeeDDLItems]);


	const payFrequencyDDLItems = useMemo(() => {
		if (payFrequencyDDL) {
			if (
				payFrequencyDDL &&
				Array.isArray(payFrequencyDDL)
			) {
				let resources: IDropDownListResponse[] =
					         sortDDLByLabel(payFrequencyDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [payFrequencyDDL]);


	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["employee-report"]);

	const updatedEDRListItems = useMemo(() => {
		return (
			edrReportData &&
			edrReportData.items &&
			edrReportData?.items.map((item: any) => ({
				...item,
			}))
		);
	}, [edrReportData]);

	const onEmployeeStatusSelectionChangeHandler = (event: any) => {
		formMethods.setValue('employeeDDL', "all")
		setEmployeeStatus(event.value);
		formMethods.setValue('employeeStatus', event.value)
	};

	// const expandedLabels = ["Reports", "Payroll "];
	// const expandededrListOrSetupItem = "EDR";
	// const enhanceData = (data: any[], parentLabel?: string): any[] => {
	// 	return data.map((item) => {
	// 		let isExpanded = expandedLabels.includes(item.label);
	// 		if (
	// 			parentLabel === "Payroll" &&
	// 			item.label === expandededrListOrSetupItem
	// 		) {
	// 			isExpanded = true;
	// 		}
	// 		const enhancedItem = {
	// 			...item,
	// 			expanded: item.items ? isExpanded : false,
	// 			items: item.items ? enhanceData(item.items, item.label) : undefined,
	// 			path: item.label === expandededrListOrSetupItem && "/reports/edr",
	// 		};
	// 		return enhancedItem;
	// 	});
	// };

			const expandedLabels = ["Reports"];
	const enhanceData = (data: any[], ancestorChain: string[] = []): any[] => {
		return data.map((item) => {
			const currentChain = [...ancestorChain, item.label];

		
			const isPayrollUnderReports =
				item.label === "Payroll" && ancestorChain.includes("Reports");

		
			const expandedEDRReportOrSetupItem =
				item.label === "EDR" &&
				ancestorChain.includes("Reports") &&
				ancestorChain.includes("Payroll");

			const isExpanded =
				expandedLabels.includes(item.label) || 
				isPayrollUnderReports ||
				expandedEDRReportOrSetupItem;

			return {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items
					? enhanceData(item.items, currentChain)
					: undefined,
				path: expandedEDRReportOrSetupItem
					? "/reports/edr"
					: item.path ?? undefined,
			};
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);



	// getting latest updated values of form fields using watch method
	const { watch } = formMethods;

	const values = watch([
		"employee",
		"type",
		"endDate",
		"payPeriod",
		"edr",
	]);

	const savePdfAsFile = (xmlData: any, filename: any) => {
		const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
		FileSaver.saveAs(blob, filename);
	};

	const onExportButtonClick = async (selectedOption: string) => {
		const [employee, type, endDate, payPeriod, edr] = values;
		try {
			setIsLoading(true);
			const response = await ReportsService.exportEDRReport(
				selectedOption,
				clientIdStore,
				taxYear,
				employee,
				type,
				endDate,
				payPeriod,
				edr,
				queryParams,
			);

			if (response) {
				if (selectedOption === "PDF") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName} - EDR-Report.pdf`;
						savePdfAsFile(response, filename);
					}
				} else if (selectedOption === "XLS") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName} - EDR-Report.xlsx`;
						savePdfAsFile(response, filename);
					}
				} else {
					const fileType =
						selectedOption === "XLS"
							? "application/vnd.ms-excel"
							: "text/csv;charset=utf-8;";
					const fileExtension = selectedOption === "XLS" ? "xls" : "csv";

					const blob = new Blob([response], { type: fileType });
					FileSaver.saveAs(blob, `${clientName} - EDR-Report.${fileExtension}`);
				}
			} else {
				console.error("Error: No data received from the server");
			}
		} catch (err) {

			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}

	};


	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>
			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"EDR Report"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<EDRReportHeader
						employeeDDLitems={employeeDDlItems}
						dateDDL={payPeriodDDL ?? []}
						onPeriodSelectionChangeHandler={(value: string) => setSelectedPeriod(value)}
						payFrequencyDDLItems={payFrequencyDDLItems && payFrequencyDDLItems}
						selectedPayPeriod={selectedPeriod}
						formMethods={formMethods}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						// isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isLoading ||
						isPending ||
						isPendingEmployeeDDLItems ||
						isPendingPayFrequencyDDL ||
						isPendingPayPeriodDDL
					}
					testId="edr-report"
				/>

				<EDRReportDataGridWithColumnToggled
					columns={
						edrReportData?.headerColumns
							? edrReportData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// edrReportData?.headerColumns ?? []
					}
					completeHeaderColumnsList={
						edrReportData
							&& edrReportData
								?.totalHeaderColumns &&
							Object.keys(edrReportData.totalHeaderColumns).length !== 0 /// comment for use hardcoded data change later
							? edrReportData.totalHeaderColumns
							: {}
						// edrReportData?.headerColumns ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						edrReportData && updatedEDRListItems
							? updatedEDRListItems
							: []
						// edrReportData?.items ?? []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					// onImportButtonClickedHandler={onImportFileClicked}
					// onImportExcelButtonClickedHandler={onExcelToPdfClicked}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						edrReportData && edrReportData.total
							? edrReportData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search..."}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"EDR Report"}
					onExportButtonClick={onExportButtonClick}
				/>

				{edrReportData ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
